import type { CanvasSource } from 'pixi.js';
import { Matrix, Rectangle, Sprite } from 'pixi.js';
import isEqual from 'lodash/isEqual';
import type { BaseGroup } from '../BaseGroup';
import { BooleanAttribute, NumberAttribute, StringAttribute } from '../attributes';
import {
    CreationType,
    DynamicStylingCreationLevel,
    TextAlignment,
    ValueMapMode,
} from '../../enums';
import type { SeekMode } from '../../enums';
import type { AssetTemplateReferenceMap, Pixels } from '../../types';
import { assertInEnum, assertMinimum, assertNumber } from '../../validators';
import { Config } from '../../video-data/config';
import { objectHasTemplateValues } from '../attributes/template/common';
import { applyGradientFillColors } from '../colorGraphicsMap';
import type { GradientFillGraphic } from '../colorGraphicsMap';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import type { RawAssetData } from '../../video-data/types';
import { PlaybackRegions, type PlaybackRegion } from '../PlaybackRegions';
import type { FontLoader } from '../FontLoader';
import { getGlobalFontManager } from '../FontManager';
import { CanvasRenderer } from './TextAsset/renderer/BaseTextRenderer';
import type { CanvasRendererSettings, GlyphCoverage } from './TextAsset/renderer/BaseTextRenderer';
import type { TextAssetStyle } from './TextAsset/TextAssetStyle';
import { BaseAsset } from './BaseAsset';
import type { BaseAssetSettings, CopyPasteData } from './BaseAsset';
import { resetSprite } from './utils';

export abstract class BaseTextAsset<Renderer extends CanvasRenderer> extends BaseAsset {
    override _classType = 'CanvasCaptionAsset';

    @NumberAttribute()
    accessor textSize = 80;

    @StringAttribute({ choices: Object.values(TextAlignment) })
    accessor textAlign = TextAlignment.LEFT;

    /**
     * Multiplier used to calculate the final leading or line height (fontSize * (1 + leadingMultiplier)).
     */
    @NumberAttribute({ min: -2, max: 2 })
    accessor leadingMultiplier = 0.1;

    @NumberAttribute({ min: 0, max: Infinity })
    accessor maxHeight = Infinity;

    /** Display the active bounds for visual debugging. */
    @BooleanAttribute({ serialize: false })
    accessor showActiveBounds = false;

    /** Display the maximum dimensions for visual debugging. */
    @BooleanAttribute({ serialize: false })
    accessor showMaxBounds = false;

    @BooleanAttribute()
    accessor hasUserChangedMaxWidth = false;

    @BooleanAttribute()
    accessor hasUserChangedMaxHeight = false;

    /** Grows the box to fit its text; suspended while an agent writes, so overlong copy clips instead of hiding. */
    @BooleanAttribute({ serialize: false })
    accessor shouldAutoResizeToFitText = true;

    @BooleanAttribute()
    accessor hasUserChangedPosition = false;

    #maxWidth = Infinity;

    #fontManager = getGlobalFontManager();

    #renderer: Renderer;
    #sprite: Sprite;
    #glyphCoverage: GlyphCoverage | null = null;
    /** The instance this text registered its coverage with, so it can leave after it is detached. */
    #glyphCoverageRegistry: Set<{ _clearGlyphCoverage(): void }> | null = null;

    #playbackRegions: Readonly<Readonly<PlaybackRegion>[]> = [];

    /**
     * Returns containers for the given level. Required for DynamicAnimationConfiguration.
     * For text assets, only object-level is supported.
     */
    getCreationContainersAtLevelForType(
        level: DynamicStylingCreationLevel,
        type = CreationType.Animation,
    ): BaseAsset[] {
        assertInEnum(type, CreationType);
        if (level === DynamicStylingCreationLevel.Object) {
            return [this];
        }
        throw new Error(`Unsupported container of type ${level}`);
    }

    constructor(container: BaseGroup, settings: BaseAssetSettings, renderer: Renderer) {
        super(container, settings);

        this.#renderer = renderer;
        this.#sprite = new Sprite();
        this._addPixiChild(this.#sprite);
        this._pixiContainer.boundsArea = new Rectangle(0, 0, 0, 0);

        if (this.maxHeight === Infinity) {
            this.maxHeight = this.getReferenceResolution().height;
        }
        if (this.maxWidth === Infinity) {
            this.maxWidth = this.getReferenceResolution().width;
        }
    }

    get fontManager() {
        return this.#fontManager;
    }

    /**
     * Some functionalities of this base class need access to style information. One
     * example is template reference map. Child classes should return whatever styles they have.
     */
    /**
     * Style information for this text asset. Child classes return whatever styles they have.
     */
    abstract get styles(): Record<string, TextAssetStyle>;

    get renderer(): Renderer {
        return this.#renderer;
    }

    /**
     * Used for captions and fancy captions, when there could be cuts in the
     * media asset or audio asset to which the captions correspond.
     * @internal
     */
    get playbackRegions(): Readonly<Readonly<PlaybackRegion>[]> {
        return this.#playbackRegions;
    }

    /**
     * @internal
     */
    set playbackRegions(value: Readonly<Readonly<PlaybackRegion>[]>) {
        if (isEqual(this.#playbackRegions, value)) {
            return;
        }
        this.#playbackRegions = value;
        this.requiresRedraw = true;
    }

    #calculateLocalBounds(): Rectangle {
        let x = this._pixiContainer.position.x;
        let y = this._pixiContainer.position.y;

        const { width: rwidth, height: rheight } = this.renderer.totalBounds;
        const width = rwidth / this.referenceToOutputMultiplier;
        const height = rheight / this.referenceToOutputMultiplier;

        // consider the anchor - note that the renderer first positions just the text and then draws additional
        // graphics, so we use the text bounds to calculate the anchor offset
        const activeTextBounds = this.renderer.activeTextBounds;
        const tWidth = activeTextBounds?.width ?? 0;
        const tHeight = activeTextBounds?.height ?? 0;
        const textWidth = tWidth / this.referenceToOutputMultiplier;
        const textHeight = tHeight / this.referenceToOutputMultiplier;
        const anchorXOffset = textWidth * this.transformation.anchor.x;
        const anchorYOffset = textHeight * this.transformation.anchor.y;
        x -= anchorXOffset;
        y -= anchorYOffset;

        const { xOffset, yOffset } = this._calculateGraphicsOffsetFromText();
        x -= xOffset;
        y -= yOffset;

        return new Rectangle(x, y, width, height);
    }

    /**
     * Calculate the offset between the text and the graphics. This is the offset from the origin (top left corner) of the
     * text to the origin (top left corner) of the graphics.
     */
    _calculateGraphicsOffsetFromText() {
        if (!this.renderer.activeTextBounds) {
            return { xOffset: 0, yOffset: 0 };
        }
        const xOffset =
            (this.renderer.activeTextBounds.x - this.renderer.totalBounds.x) /
            this.referenceToOutputMultiplier;
        const yOffset =
            (this.renderer.activeTextBounds.y - this.renderer.totalBounds.y) /
            this.referenceToOutputMultiplier;
        return { xOffset, yOffset };
    }

    override get boundsWithoutAnimation() {
        if (this.hasText) {
            return super.boundsWithoutAnimation;
        }
        // The bounds will be an empty rectangle centered at (0,0) if the text is empty.
        // But returning a rectangle that's centered according to our transformation will help
        // place NTE's empty state box in a more accurate place on screen.
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = transformation.position;
        return new Rectangle(x, y, 0, 0);
    }

    /**
     * Height of the pure rendered text in scene-reference units (no box-graphic padding, no
     * animation scale, no cached PIXI bounds).
     */
    get renderedTextHeight(): number {
        if (!this.hasText || !this.renderer.canRender) {
            return 0;
        }
        const activeBounds = this.renderer.activeTextBounds;
        if (!activeBounds) {
            return 0;
        }
        return activeBounds.height / this.referenceToOutputMultiplier;
    }

    /**
     * Since the transformation information is spread between PIXI's container and the renderer, we need to manually
     * calculate the local bounds of the text asset. This is done by calculating the bounds of the renderer and then
     * converting it to the local space of the container.
     */
    override getLocalBounds = (_rect?: Rectangle): Rectangle => {
        this.updateTransform();

        if (!this.renderer.canRender) {
            const x = this._pixiContainer.localTransform.tx;
            const y = this._pixiContainer.localTransform.ty;
            return new Rectangle(x, y, 0, 0);
        }
        return this.#calculateLocalBounds();
    };

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        this._pixiContainer.alpha = this.opacity;
        if (this.isPositionControlledByParent) {
            return;
        }
        this._pixiContainer.x = this.transformation.position.x + offsetX;
        this._pixiContainer.y = this.transformation.position.y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
        this.updateTransform();
    }

    /**
     * Traditional PIXI containers apply transformations to their children. This is not the case for our text canvas
     * assets. We need to apply the transformation to the renderer instead. We do this by negating any container
     * transformation so the PIXI container always sits in the top-left corner (0-0) and thus fills up the entire
     * scene. Once that is done we then move on to apply the transformation to the renderer. This makes it seem like
     * the text asset is being transformed as part of the PIXI hierarchy when in reality it's the renderer that is being
     * transformed.
     * */
    override updateTransform = (_opts?: Record<string, unknown>): this => {
        const parentGlobalTransform =
            this.parent?._pixiContainer.getGlobalTransform(new Matrix(), false) ?? new Matrix();
        const x = parentGlobalTransform.tx + this._pixiContainer.position.x;
        const y = parentGlobalTransform.ty + this._pixiContainer.position.y;

        this._pixiContainer.updateLocalTransform();
        const inverseContainerTransform = parentGlobalTransform
            .clone()
            .append(this._pixiContainer.localTransform)
            .invert();
        for (const child of this._pixiContentContainer.children) {
            child.setFromMatrix(inverseContainerTransform);
        }

        if (!this.renderer.canRender) {
            this._pixiContainer.boundsArea = new Rectangle(0, 0, 0, 0);
            this._invalidateBoundsCache();
            return this;
        }
        // now move the world transformation into the renderer
        this.renderer.translateX = (x * this.referenceToOutputMultiplier) as Pixels;
        this.renderer.translateY = (y * this.referenceToOutputMultiplier) as Pixels;
        this.renderer.anchorX = this.transformation.anchor.x;
        this.renderer.anchorY = this.transformation.anchor.y;
        this.renderer.scaleX = this.scale.x;
        this.renderer.scaleY = this.scale.y;

        const { width: rwidth, height: rheight } = this.renderer.totalBounds;
        const boundsWidth = rwidth / this.referenceToOutputMultiplier;
        const boundsHeight = rheight / this.referenceToOutputMultiplier;

        const activeTextBounds = this.renderer.activeTextBounds;
        const tWidth = activeTextBounds?.width ?? 0;
        const tHeight = activeTextBounds?.height ?? 0;
        const textWidth = tWidth / this.referenceToOutputMultiplier;
        const textHeight = tHeight / this.referenceToOutputMultiplier;
        const anchorXOffset = textWidth * this.transformation.anchor.x;
        const anchorYOffset = textHeight * this.transformation.anchor.y;

        const { xOffset, yOffset } = this._calculateGraphicsOffsetFromText();

        this._pixiContainer.boundsArea = new Rectangle(
            -anchorXOffset - xOffset,
            -anchorYOffset - yOffset,
            boundsWidth,
            boundsHeight,
        );
        this._invalidateBoundsCache();

        return this;
    };

    /**
     * Height of a single line of this text, in scene-reference units.
     * @internal
     */
    get _oneLineFloor(): number {
        return this.textSize * (1 + this.leadingMultiplier) + Config.LINE_HEIGHT_RENDER_SLACK;
    }

    /**
     * The height the box takes up; a subclass whose text can grow past `maxHeight` reports that.
     * @internal
     */
    protected get _occupiedHeight(): number {
        return this.maxHeight;
    }

    /**
     * Approximate the maximum bounds of the text asset. This only considers the max area of the
     * text and does not include any graphics, which may extend beyond the text.
     */
    override get approximateMaxBounds(): Rectangle {
        const transformation = this.getFreelyPositionedParent().transformation;
        const height = this._occupiedHeight;
        const { x, y } = getPositionFromTransformation(transformation, this.maxWidth, height);
        return new Rectangle(x, y, this.maxWidth, height);
    }

    /**
     * Returns true if the renderer has something to render. This is used to determine if we should render or not. The
     * renderer is dependent on text metrics and style metrics. If either of these are empty, then nothing can be rendered.
     */
    get canRender(): boolean {
        return this.renderer.canRender;
    }

    abstract get hasText(): boolean;

    get maxWidth(): number {
        return this.#maxWidth;
    }

    set maxWidth(value: number) {
        assertNumber(value);
        assertMinimum(value, 0);
        if (this.maxWidth !== value) {
            this.#maxWidth = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Adjusts the text asset's max width to ensure it stays within the resolution boundaries.
     */
    adjustMaxWidthToFitResolution(offset: number = 0): void {
        const { x: currentMaxTextX, width: currentMaxTextWidth } = this.approximateMaxBounds;
        const resolutionWidth = this.getReferenceResolution().width;

        offset += this._calculateGraphicsOffsetFromText().xOffset;

        if (currentMaxTextX < 0) {
            // If text starts before viewport, adjust maxWidth to fit from x=0
            this.maxWidth = Math.floor(currentMaxTextWidth + currentMaxTextX - offset);
            return;
        }
        if (currentMaxTextX + currentMaxTextWidth > resolutionWidth) {
            // If text extends beyond right edge, adjust to fit within resolution width
            this.maxWidth = Math.floor(resolutionWidth - currentMaxTextX - offset);
        }
    }

    protected override _build(): void {
        super._build();

        if (this.visible === false || !this.hasText) {
            this.#renderer.resetBuild();
            this._emitAssetCanPlay();
            return;
        }

        void this.ensureUsedFontsAreAdded();
        if (!this.isDoneTryingToLoad) {
            this.sentCanPlay = false;
            return;
        }

        const state = this.getRendererSettings();
        this.#renderer.apply(state);
        this.#renderer.build();
        // we render our text at the output resolution so we need to scale the texture up to the reference resolution here

        // apply transformation
        this.applyTransformation();

        this._emitAssetCanPlay();
    }

    /** @internal */
    _ensureRendererBuilt(): void {
        if (this.visible === false || !this.hasText || !this.isDoneTryingToLoad) {
            return;
        }
        if (!this.requiresRedraw) {
            return;
        }
        if (!CanvasRenderer.isTextRenderingSupported) {
            return;
        }
        this.#renderer.apply(this.getRendererSettings());
        this.#renderer.build();
    }

    override get isDoneTryingToLoad(): boolean {
        if (!super.isDoneTryingToLoad) {
            return false;
        }
        if (!this._shouldLoad) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        if (this.hasAllNecessaryFonts) {
            return true;
        }
        return false;
    }

    override get _shouldLoad(): boolean {
        if (!this.visible) {
            return false;
        }
        if (typeof this.fetchResources !== 'boolean') {
            return this.fetchResources.fonts ?? false;
        }
        return super._shouldLoad;
    }

    override _hasTemplateReferenceValues() {
        return Object.values(this.styles).some((style) => objectHasTemplateValues(style));
    }

    /**
     *  Ensures all the fonts used by this asset are added to the font manager.
     *  Returns a list of all used fonts.
     */
    ensureUsedFontsAreAdded(): FontLoader[] {
        if (!this._shouldLoad) {
            return [];
        }
        return Object.values(this.styles).map((style) => this.fontManager.addFont(style.fontUrl));
    }

    /**
     * The characters this asset draws. Fallback fonts are only loaded for text that has a
     * character one of them would draw.
     */
    protected abstract get _fallbackFontText(): string;

    /**
     * Starts loading the fallback fonts this asset's text needs, returning them. Unlike the style
     * fonts, a fallback that fails to load doesn't fail the asset: it draws without the glyph.
     */
    ensureFallbackFontsAreAdded(): FontLoader[] {
        if (!this._shouldLoad || !this.hasText) {
            return [];
        }
        return this.fontManager.addFallbackFontsForText(this._fallbackFontText);
    }

    override get _skipBuildAndDraw(): boolean {
        return !CanvasRenderer.isTextRenderingSupported;
    }

    /**
     * Resolves once all the fonts used by this asset are loaded.
     */
    async onUsedFontsLoaded(): Promise<void[]> {
        const fontUrls = [
            ...Object.values(this.styles).map((style) => style.fontUrl),
            ...this.ensureFallbackFontsAreAdded().map((font) => font.sourceUrl),
        ];
        return Promise.all(
            fontUrls.map(async (fontUrl) => {
                await this.fontManager.getFontBySourceOnceLoaded(fontUrl);
            }),
        );
    }

    get hasAllNecessaryFonts(): boolean {
        return (
            this.ensureUsedFontsAreAdded().every((font) => font.isLoaded) &&
            this.ensureFallbackFontsAreAdded().every((font) => font.isDoneTryingToLoad)
        );
    }

    someFontsDidNotLoadYet(): boolean {
        return this.ensureUsedFontsAreAdded().some((font) => !font.isLoaded);
    }

    someFontsFailedToLoad(): boolean {
        return this.ensureUsedFontsAreAdded().some((font) => font.failedToLoad);
    }

    usedFontWasAdded(sourceUrl: string): boolean {
        return this.ensureUsedFontsAreAdded().some((font) => font.sourceUrl === sourceUrl);
    }

    override get failedToLoad(): boolean {
        if (super.failedToLoad) {
            return true;
        }
        if (!this._shouldLoad) {
            return false;
        }
        if (!this.hasText) {
            return false;
        }
        if (this.someFontsDidNotLoadYet()) {
            return true;
        }
        return this.someFontsFailedToLoad();
    }

    override get isLoaded(): boolean {
        if (!super.isLoaded) {
            return false;
        }
        if (!this._shouldLoad) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        return this.hasAllNecessaryFonts;
    }

    _redrawOnFontLoad() {
        // We could optimize this so the text doesn't rebuild if this asset isn't using the font.
        // However, we need to consider both the style fonts and the fallback fonts to do this. With the
        // canvas api it's not easy to know which font is used, so for now we always rebuild when a font loads.

        // A new font has loaded, we need to rebuild the canvas, so it can access the new font.

        this.requiresRedraw = true;
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    protected getRendererSettings(): CanvasRendererSettings {
        return {
            maxTextWidth: (this.maxWidth * this.referenceToOutputMultiplier) as Pixels,
            maxTextHeight: (this.maxHeight * this.referenceToOutputMultiplier) as Pixels,
            showActiveBounds: this.showActiveBounds,
            showMaxBounds: this.showMaxBounds,
        };
    }

    /**
     * Selectivly applies style properties to the text asset. This includes the transformation, max width, max height, text size, and alignment.
     * @internal
     */
    _loadStylePropertiesForSwap(data: RawAssetData) {
        if (data?.transformation) {
            this.transformation.load(data.transformation as Record<string, unknown>);
        }
        if (data?.maxWidth) {
            this.maxWidth = data.maxWidth as number;
        }
        if (data?.maxHeight) {
            this.maxHeight = data.maxHeight as number;
        }
        if (data?.textSize) {
            this.textSize = data.textSize as number;
        }
        if (data?.textAlign) {
            this.textAlign = data.textAlign as TextAlignment;
        }
    }

    /**
     * Override applyStyleProperties to clear dynamic styling before loading new style
     */
    override pasteStyleProperties(settings: CopyPasteData) {
        Object.values(this.styles).map((style) => (style as TextAssetStyle).removeDynamicStyles());
        super.pasteStyleProperties(settings);
    }

    /**
     * Builds a map of all values referencing template value in object and its styles.
     */
    override buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        let result = super.buildTemplateReferenceMap();
        Object.entries(this.styles).forEach(([styleName, style]) => {
            result = {
                ...(style._buildValueMap(
                    ValueMapMode.GET_REFERENCE_VALUES,
                    styleName,
                ) as AssetTemplateReferenceMap),
                ...result,
            };
        });
        return result;
    }

    /**
     * Builds a map of color values of all color graphics in the object (including all color graphics of all styles in
     * the object). When no graphics are found, an empty object is returned.
     */
    buildColorGraphicsMap(): Record<string, number> {
        let result: Record<string, unknown> = {};
        const colorMap: Record<string, number> = {};

        Object.entries(this.styles).forEach(([styleName, style]) => {
            const map = style._buildValueMap(
                ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE,
                styleName,
                true,
            );
            result = {
                ...result,
                ...map,
            };
        });

        Object.entries(result).map(([valueKey, value]) => {
            if (
                (valueKey.toLowerCase().includes('fill') ||
                    valueKey.toLowerCase().includes('color')) &&
                !valueKey.toLowerCase().includes('alpha') &&
                valueKey.toLowerCase().includes('graphics')
            ) {
                colorMap[valueKey] = value as number;
            }
        });

        // For gradient fills, expose the start/end stop colours instead of the solid fill colour.
        Object.entries(this.styles).forEach(([styleName, style]) => {
            style.graphics.forEach((graphic, index) => {
                if (graphic.asMask) {
                    return;
                }
                applyGradientFillColors(
                    colorMap,
                    graphic as unknown as GradientFillGraphic,
                    `${styleName}.graphics.${index}`,
                    'fill',
                );
            });
        });

        return colorMap;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        if (this.visible === false || !this.hasText) {
            return;
        }
        this.#renderer.seekMode = seekMode;
        const duration = PlaybackRegions.getDurationFromPlaybackRegions(this.#playbackRegions);
        const playbackTime = progress * duration;
        this.#renderer.currentTime = PlaybackRegions.playbackTimeToAssetTime(
            this.#playbackRegions,
            Infinity, // progress cannot be past the end of the last playback region
            playbackTime,
        );
    }

    /**
     * Keeps, from every render, how much of each output pixel this text's glyphs cover, without its
     * graphics, shadows or colour, so a contrast check can read the glyphs straight off a normal
     * render instead of re-rendering with the text recoloured. Read it through `glyphCoverage`.
     */
    captureGlyphCoverage(enabled: boolean) {
        this.#glyphCoverage = enabled
            ? (this.#glyphCoverage ?? {
                  normal: document.createElement('canvas'),
                  highlight: document.createElement('canvas'),
              })
            : null;
        this.#renderer.glyphCoverage = this.#glyphCoverage;
        this.#glyphCoverageRegistry?.delete(this);
        this.#glyphCoverageRegistry = enabled ? this.root._glyphCoverageAssets : null;
        this.#glyphCoverageRegistry?.add(this);
    }

    /**
     * Empties the coverage before a render, so a text the render does not draw, including one in a
     * scene that is not active, reads as covering nothing rather than keeping an earlier render's.
     */
    _clearGlyphCoverage() {
        if (!this.#glyphCoverage) {
            return;
        }
        for (const canvas of [this.#glyphCoverage.normal, this.#glyphCoverage.highlight]) {
            canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    /** The glyph coverage from the last render, sized like the output; null unless it was asked for. */
    get glyphCoverage(): GlyphCoverage | null {
        return this.#glyphCoverage;
    }

    override updateForRender() {
        super.updateForRender();

        if (this.visible === false) {
            return;
        }

        if (!this.hasText) {
            return;
        }

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const canvas = (this.#sprite.texture.source as CanvasSource).resource as HTMLCanvasElement;
        this.#renderer.setCanvas(canvas);
        // NOTE: even though updateTransform is called as part of the PIXI render loop, we need to manually call it
        // here beforehand so our renderer can render the text at the correct position before it gets drawn by PIXI.
        // That's because updateTransform decomposes the transformation and transfers it to the canvas renderer.
        this.updateTransform();
        this.#renderer.draw();

        this.#sprite.texture.source.update();
    }

    /**
     * Draws the rendered text's glyph alpha into the matte context; the full-scene text canvas maps
     * 1:1 to the matte, so a referencing asset shows through the letter shapes.
     */
    protected override _drawMatteShape(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
    ): boolean {
        if (!this.hasText || !this.renderer.canRender) {
            return false;
        }
        const source = this.#sprite.texture.source as CanvasSource | undefined;
        const textCanvas = source?.resource as HTMLCanvasElement | undefined;
        if (!textCanvas) {
            return false;
        }
        context.drawImage(textCanvas, 0, 0, width, height);
        return true;
    }

    override destroy() {
        this.#glyphCoverageRegistry?.delete(this);
        this.#glyphCoverageRegistry = null;
        super.destroy(false);
        this.#renderer.destroy();
        this.#sprite.destroy(false);
    }

    override get propertiesToSerialize() {
        // WARNING: we can't use getAttributesToSerialize on base classes because it will return an empty array for all
        // child classes. This is limitation of current attribute system. That's why we need to manually add them here.
        return super.propertiesToSerialize.concat([
            'maxWidth',
            'maxHeight',
            'textSize',
            'textAlign',
            'leadingMultiplier',
            'hasUserChangedMaxWidth',
            'hasUserChangedMaxHeight',
            'hasUserChangedPosition',
        ]);
    }

    override get editableProperties() {
        return super.editableProperties.concat(['textSize']);
    }
}
